import React, { useState, useEffect } from 'react'
import ProgramEditForm from './ProgramEditForm'
import PersonDetailModal from '../People/PersonDetailModal'
import api from '../../../services/api'
import { normalizeProgramImage } from '../../../utils/normalizePersonImage'
import '../../../css/programOverlay.css'

function BackgroundHeader({ imageUrl, title }) {
  return (
    <header
      key={imageUrl}
      className="overlay-header"
      style={{
        backgroundImage: `url(${imageUrl || 'https://via.placeholder.com/200'})`,
      }}
    >
      <h2>{title}</h2>
    </header>
  )
}

const ProgramOverlay = ({
  program,
  schedules,
  onClose,
  onEdit,
  peopleList,
  topicList,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [detailPerson, setDetailPerson] = useState(null)

  const [editData, setEditData] = useState({
    title: program.title,
    description: program.description,
    imageUrl: program.image_url || program.imageUrl,
    imageBase64: '',
    episodeLogoUrl: program.episode_logo || program.episodeLogo || '',
    episodeLogoBase64: '',
  })

  const [protagonists, setProtagonists] = useState(program.protagonists?.map(p => p.id) || [])
  const [expectedGuests, setExpectedGuests] = useState(program.expectedGuests?.map(p => p.id) || [])
  const [topics, setTopics] = useState(program.topics?.map(t => t.id) || [])

  const [preview, setPreview] = useState(null)
  const [episodeLogoPreview, setEpisodeLogoPreview] = useState(null)
  const [imageInfo, setImageInfo] = useState(null)
  const [episodeLogoInfo, setEpisodeLogoInfo] = useState(null)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [isEpisodeLogoLoading, setIsEpisodeLogoLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handlePersonClick = async (person) => {
    try {
      // Fetch della persona completa con qualifications
      const response = await api.get(`/people/${person.id}`)
      setDetailPerson(response.data)
    } catch (error) {
      console.error('Error loading person details:', error)
      // Fallback: usa i dati disponibili
      setDetailPerson(person)
    }
  }

  useEffect(() => {
    setEditData({
      title: program.title,
      description: program.description,
      imageUrl: program.image_url || program.imageUrl,
      imageBase64: '',
      episodeLogoUrl: program.episode_logo || program.episodeLogo || '',
      episodeLogoBase64: '',
    })
    setProtagonists(program.protagonists?.map(p => p.id) || [])
    setExpectedGuests(program.expectedGuests?.map(p => p.id) || [])
    setTopics(program.topics?.map(t => t.id) || [])
    setPreview(null)
    setEpisodeLogoPreview(null)
    setImageInfo(null)
    setEpisodeLogoInfo(null)
    setError('')
  }, [program])

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditData({
      title: program.title,
      description: program.description,
      imageUrl: program.image_url || program.imageUrl,
      imageBase64: '',
      episodeLogoUrl: program.episode_logo || program.episodeLogo || '',
      episodeLogoBase64: '',
    })
    setProtagonists(program.protagonists?.map(p => p.id) || [])
    setExpectedGuests(program.expectedGuests?.map(p => p.id) || [])
    setTopics(program.topics?.map(t => t.id) || [])
    setPreview(null)
    setEpisodeLogoPreview(null)
    setImageInfo(null)
    setEpisodeLogoInfo(null)
    setError('')
  }

  const handleChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value })
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) {
      setEditData(prev => ({ ...prev, imageBase64: '' }))
      setPreview(null)
      setImageInfo(null)
      return
    }

    setError('')
    setIsImageLoading(true)
    try {
      const normalized = await normalizeProgramImage(file)
      setEditData(prev => ({ ...prev, imageBase64: normalized.dataUrl }))
      setPreview(normalized.dataUrl)
      setImageInfo(normalized)
    } catch (err) {
      console.error('Errore nella normalizzazione della copertina:', err)
      setEditData(prev => ({ ...prev, imageBase64: '' }))
      setPreview(null)
      setImageInfo(null)
      setError(err?.message || 'Impossibile preparare la copertina.')
      e.target.value = ''
    } finally {
      setIsImageLoading(false)
    }
  }

  const handleEpisodeLogoFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) {
      setEditData(prev => ({ ...prev, episodeLogoBase64: '' }))
      setEpisodeLogoPreview(null)
      setEpisodeLogoInfo(null)
      return
    }

    setError('')
    setIsEpisodeLogoLoading(true)
    try {
      const normalized = await normalizeProgramImage(file)
      setEditData(prev => ({ ...prev, episodeLogoBase64: normalized.dataUrl }))
      setEpisodeLogoPreview(normalized.dataUrl)
      setEpisodeLogoInfo(normalized)
    } catch (err) {
      console.error('Errore nella normalizzazione del logo episodio:', err)
      setEditData(prev => ({ ...prev, episodeLogoBase64: '' }))
      setEpisodeLogoPreview(null)
      setEpisodeLogoInfo(null)
      setError(err?.message || 'Impossibile preparare il logo episodio.')
      e.target.value = ''
    } finally {
      setIsEpisodeLogoLoading(false)
    }
  }

  const handleSaveEdit = async () => {
    if (isImageLoading || isEpisodeLogoLoading || isSubmitting) return

    setError('')
    setIsSubmitting(true)
    const payload = {
      title: editData.title,
      description: editData.description,
      imageBase64: editData.imageBase64,
      episodeLogoBase64: editData.episodeLogoBase64,
      protagonists,
      expectedGuests,
      topics,
    }
    try {
      await onEdit(program.id, payload)
      setIsEditing(false)
      setPreview(null)
      setEpisodeLogoPreview(null)
      setImageInfo(null)
      setEpisodeLogoInfo(null)
    } catch (err) {
      const message = err?.code === 'ECONNABORTED'
        ? 'Il caricamento ha impiegato troppo tempo. Riprova senza chiudere la finestra.'
        : err?.response?.data?.error || 'Errore durante il salvataggio del programma.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const addProtagonist = (id) => {
    if (id && !protagonists.includes(id)) {
      setProtagonists([...protagonists, id])
    }
  }
  const removeProtagonist = (id) => {
    setProtagonists(protagonists.filter(p => p !== id))
  }

  const addExpectedGuest = (id) => {
    if (id && !expectedGuests.includes(id)) {
      setExpectedGuests([...expectedGuests, id])
    }
  }
  const removeExpectedGuest = (id) => {
    setExpectedGuests(expectedGuests.filter(p => p !== id))
  }

  const addTopic = (id) => {
    if (id && !topics.includes(id)) {
      setTopics([...topics, id])
    }
  }
  const removeTopic = (id) => {
    setTopics(topics.filter(t => t !== id))
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const d = new Date(dateString)
    return d.toLocaleDateString()
  }

  const displayScheduleDate = (sch) => {
    return sch.isRecurring ? sch.dayOfWeek : formatDate(sch.date)
  }


  return (
    <div className="overlay-container" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>

        <BackgroundHeader
          imageUrl={program.image_url || program.imageUrl}
          title={program.title}
        />

        <div className="overlay-body">
          {isEditing ? (
            <ProgramEditForm
              editData={editData}
              onChange={handleChange}
              onFileChange={handleFileChange}
              onEpisodeLogoFileChange={handleEpisodeLogoFileChange}
              preview={preview}
              episodeLogoPreview={episodeLogoPreview}
              imageInfo={imageInfo}
              episodeLogoInfo={episodeLogoInfo}
              isImageLoading={isImageLoading}
              isEpisodeLogoLoading={isEpisodeLogoLoading}
              isSubmitting={isSubmitting}
              error={error}
              protagonists={protagonists}
              expectedGuests={expectedGuests}
              topics={topics}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
              peopleList={peopleList}
              topicList={topicList}
              addProtagonist={addProtagonist}
              removeProtagonist={removeProtagonist}
              addExpectedGuest={addExpectedGuest}
              removeExpectedGuest={removeExpectedGuest}
              addTopic={addTopic}
              removeTopic={removeTopic}
            />
          ) : (
            <>
              <p className="program-description">{program.description}</p>

              {program.protagonists?.length > 0 && (
                <div className="modal-section">
                  <h4>Protagonisti:</h4>
                  <div className="chip-container">
                    {program.protagonists.map(person => (
                      <div
                        key={person.id}
                        className="chip"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePersonClick(person);
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Clicca per visualizzare i dettagli"
                      >
                        <img
                          src={person.imageUrl || 'https://via.placeholder.com/30'}
                          alt={`${person.nome} ${person.cognome}`}
                        />
                        <span>{person.nome} {person.cognome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {program.expectedGuests?.length > 0 && (
                <div className="modal-section">
                  <h4>Expected Guests:</h4>
                  <div className="chip-container">
                    {program.expectedGuests.map(person => (
                      <div
                        key={person.id}
                        className="chip"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePersonClick(person);
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Clicca per visualizzare i dettagli"
                      >
                        <img
                          src={person.imageUrl || 'https://via.placeholder.com/30'}
                          alt={`${person.nome} ${person.cognome}`}
                        />
                        <span>{person.nome} {person.cognome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {program.topics?.length > 0 && (
                <div className="modal-section">
                  <h4>Topics:</h4>
                  <div className="chip-container">
                    {program.topics.map(topic => (
                      <div key={topic.id} className="chip topic-chip">
                        <span>{topic.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-section">
                <h4>Schedule:</h4>
                {schedules
                  .filter(sch => Number(sch.programId) === Number(program.id))
                  .map(sch => (
                    <div key={sch.id} className="schedule-item">
                      <span>{displayScheduleDate(sch)} </span>
                      <span>{sch.startTime} - {sch.endTime}</span>
                    </div>
                  ))}
              </div>


              <div className="modal-actions">
                <button onClick={() => setIsEditing(true)}>Modifica</button>
              </div>
            </>
          )}
        </div>

        {detailPerson && (
          <PersonDetailModal
            person={detailPerson}
            onClose={() => setDetailPerson(null)}
          />
        )}
      </div>
    </div>
  )
}

export default ProgramOverlay
